import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  establishmentContact,
  establishmentCurrency,
  establishmentInit,
  establishmentLocation,
  establishmentUploads,
} from "../../../../helpers/dummyConstants";
import {
  Box,
  ButtonStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import { FormDataContainer, SettingsWrapper } from "./styled";
import { EditIcon } from "../../../../components/styled/AllImages";
import InfoData from "../../../../components/common/infoData/InfoData";
import SettingsAPI from "../../../../services/api/settingsApi";

const Establishment = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const [estInit, setEstInit] = useState(establishmentInit);
  const [estLocation, setEstLocation] = useState(establishmentLocation);
  const [estContact, setEstContact] = useState(establishmentContact);
  const [estUploads, setEstUploads] = useState(establishmentUploads);
  const [estCurrencies, setEstCurrencies] = useState(establishmentCurrency);
  const [branchesRows, setBranchesRows] = useState([]);
  const [headquartersRows, setHeadquartersRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const getEstablishmentInfo = async () => {
    try {
      setLoading(true);
      const { data, code } = await SettingsAPI.getEstablishmentInfo();
      if (data) {
        const updatedEstInit = establishmentInit.map((item) => {
          const key = item.label; // Match the label with the key in the API response
          let updatedText;
          if (key === "type") {
            updatedText = language === "en" ? data?.organization_type?.en_name : data?.organization_type?.ar_name;
          }
          if (key === "Sector") {
            updatedText = data?.sector;
          }
          if (key === "name") {
            updatedText = data?.name;
          }
          if (key === "description") {
            updatedText = data?.description;
          }
          return { ...item, text: updatedText };
        });
        setEstInit(updatedEstInit);

        const updatedEstLocation = establishmentLocation.map((item) => {
          const key = item.label; // Match the label with the key in the API response
          let updatedText;
          if (key === "Country") {
            updatedText = data?.country?.name;
          }
          if (key === "City") {
            updatedText = language === "en" ? data?.city?.en_name : data?.city?.ar_name;
          }
          if (key === "Address") {
            updatedText = data?.address;
          }
          return { ...item, text: updatedText };
        });
        setEstLocation(updatedEstLocation);

        const updatedEstContact = establishmentContact.map((item) => {
          const key = item.label; // Match the label with the key in the API response
          let updatedText;
          if (key === "ContactNo") {
            updatedText = data?.contact_phone;
          }
          if (key === "email") {
            updatedText = data?.contact_email;
          }
          if (key === "ContactPerson") {
            updatedText = data?.contact_person;
          }
          return { ...item, text: updatedText };
        });
        setEstContact(updatedEstContact);

        const baseAssetURL = process.env.REACT_APP_ASSET_URL;
        const logoUrl = data?.logo ? `${baseAssetURL}/${data.logo}` : null;
        const updatedEstUploads = establishmentUploads.map((item) => {
          if (item.label === "Logo") {
            return {
              ...item,
              render: () => (
                <Box className="upload-image-full">
                  {logoUrl ? <img src={logoUrl} alt="Logo" /> : <span></span>}
                </Box>
              ),
            };
          }
          return item;
        });
        setEstUploads(updatedEstUploads);
        const updatedCurrency = establishmentCurrency?.map((item) => {
          if (item.label === "currency") {
            return {
              ...item,
              text: data?.currency?.currency,
            };
          }
          if (item.label === "tax_number") {
            return {
              ...item,
              text: data?.vat_number,
            };
          }
          if (item.label === "tax_percentage") {
            return {
              ...item,
              text: data?.vat_percentage,
            };
          }
          return item;
        });
        setEstCurrencies(updatedCurrency)
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEstablishmentInfo();
  }, []);

  return (
    <SettingsWrapper>
      <Box className="content-head">
        <SubHeadingStyled size="16px" color="var(--text)">
          {t("settings")}
        </SubHeadingStyled>
      </Box>
      {loading ? (
        <ThemeLoader />
      ) : (
        <Box className="content-container">
          <Box className="d-flex justify-content-end">
            <ButtonStyled
              label={t("edit")}
              iconBefore={EditIcon}
              color="var(--white)"
              onClick={() => navigate("/settings/establishment/edit")}
            />
          </Box>
          <Row className="mt-3">
            <Col md={12}>
              <FormDataContainer className="dataContainer">
                {estInit.map((view, i) => (
                  <InfoData
                    key={view.id}
                    label={view.label}
                    text={view.text}
                    render={view.render ? view.render : null}
                  />
                ))}
                <Box className="innerFoHead">
                  <SubHeadingStyled size="16px" color="var(--text)">
                    {t("location")}
                  </SubHeadingStyled>
                </Box>
                {estLocation.map((view, i) => (
                  <InfoData
                    key={view.id}
                    label={view.label}
                    text={view.text}
                    render={view.render ? view.render : null}
                  />
                ))}
                <Box className="innerFoHead">
                  <SubHeadingStyled size="16px" color="var(--text)">
                    {t("ContactDetails")}
                  </SubHeadingStyled>
                </Box>
                {estContact.map((view, i) => (
                  <InfoData
                    key={view.id}
                    label={view.label}
                    text={view.text}
                    render={view.render ? view.render : null}
                  />
                ))}
                <Box className="innerFoHead">
                  <SubHeadingStyled size="16px" color="var(--text)">
                    {t("currency")}
                  </SubHeadingStyled>
                </Box>
                {estCurrencies.map((view, i) => (
                  <InfoData
                    key={view.id}
                    label={view.label}
                    text={view.text}
                    render={view.render ? view.render : null}
                  />
                ))}
                <Box className="innerFoHead">
                  <SubHeadingStyled size="16px" color="var(--text)">
                    {t("Uploads")}
                  </SubHeadingStyled>
                </Box>
                {estUploads.map((view) => (
                  <InfoData
                    key={view.id}
                    label={view.label}
                    text={view.text}
                    image={view.image}
                    render={view.render ? view.render : null}
                  />
                ))}
              </FormDataContainer>
            </Col>
          </Row>
        </Box>
      )}
    </SettingsWrapper>
  );
};

export default Establishment;
